import { getPolylines, MultiLocationAutocompleteModal, useIsMobile, useSubmitButton, FiltersModal, FilterButton } from './chunk-E5YV2QM5.js';
import { WithFormProvider } from './chunk-6IXBTFSD.js';
import './chunk-LOGDY5AH.js';
import './chunk-RWGAIR3F.js';
import './chunk-DWUCGXIQ.js';
import { WithMFEContext, getInternalServiceUrl, getServiceUrl, useTemplates, useTransformers, SearchProvider, fetchPlaces, useConditionalFilters, useUpdateLocations, ModalProvider, useVariant, useTranslate } from './chunk-UUWHMO43.js';
import { mapSearchModelToFormModel, mapFormModelToSearchModel } from '@aviv-search/transformers';
import { Skeleton } from '@gemini/ui';
import { Box, useTokens } from '@gemini/core';
import { useState, useMemo, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { css } from '@emotion/css';
import { SlidersIcon, MagnifyingGlassIcon } from '@gemini/icons';
import { cloneDeep } from 'lodash';
import { LocationType, RoomsType, ROOMS_DICTIONARY } from '@aviv-search/types';
import { jsx, jsxs } from 'react/jsx-runtime';

// src/containers/FiltersBar/helpers/getLabels.ts
var getLabels = () => ({
  distributionAndEstateTypeLabel: useTranslate("filtersBar.title.project"),
  spaceLabel: useTranslate("filtersBar.title.livingArea"),
  otherFiltersLabel: useTranslate("filtersBar.title.filters"),
  priceLabel: useTranslate("filtersBar.title.price"),
  roomsLabel: useTranslate("filtersBar.title.rooms")
});
var getDistributionAndEstateTypeTitle = ({ distributionType, estateType }) => useTranslate("filtersBar.label.project", {
  estate_type: `${estateType}`.toLowerCase(),
  distribution_type: `${distributionType}`.toLowerCase()
});
var getMinMaxTitle = (minMax, suffix) => {
  if (!minMax || !minMax.min && !minMax.max)
    return void 0;
  if (minMax.min && minMax.max)
    return `${minMax.min} - ${minMax.max} ${suffix}`;
  const value = minMax.min ? minMax.min : minMax.max;
  const wording = useTranslate(`filters.common.range.label.${minMax.min ? "min" : "max"}`).toLowerCase();
  return `${wording} ${value} ${suffix}`;
};
var getRoomsMinMaxTitle = (minMax) => {
  const roomsLabel = useTranslate("filtersBar.title.rooms").toLowerCase();
  if (!minMax || minMax.min === RoomsType.ANY && minMax.max === RoomsType.ANY)
    return void 0;
  if (minMax.min !== RoomsType.ANY && minMax.max !== RoomsType.ANY) {
    return `${ROOMS_DICTIONARY[minMax.min].label} - ${ROOMS_DICTIONARY[minMax.max].label} ${roomsLabel}`;
  }
  const value = minMax.min !== RoomsType.ANY ? ROOMS_DICTIONARY[minMax.min].label : ROOMS_DICTIONARY[minMax.max].label;
  const wording = useTranslate(
    `filters.common.range.label.${minMax.min === RoomsType.ANY ? "max" : "min"}`
  ).toLowerCase();
  return `${wording} ${value} ${roomsLabel}`;
};
var getPriceTitle = (model) => getMinMaxTitle(model.price, "\u20AC");
var getRoomsTitle = (model) => getRoomsMinMaxTitle(model.rooms);
var getSpaceTitle = (model) => getMinMaxTitle(model.space, "m\xB2");
var getOtherFiltersTitle = ({ features = [] }) => features.length > 0 ? useTranslate("filtersBar.title.filters") : void 0;
var getTitles = (model) => ({
  distributionAndEstateTypeTitle: getDistributionAndEstateTypeTitle(model),
  priceTitle: getPriceTitle(model),
  roomsTitle: getRoomsTitle(model),
  spaceTitle: getSpaceTitle(model),
  otherFiltersTitle: getOtherFiltersTitle(model)
});
var spacing = "spacing.12";
var DefaultNoneDisplayedBox = ({ children, unsafeClassName }) => /* @__PURE__ */ jsx(Box, { display: "none", unsafe_className: unsafeClassName, children });
var filterTestId = "search-mfe-filtersbar-filter-button";
var distributionTypeTestId = "search-mfe-filtersbar-distributiontype-button";
var priceRangeTestId = "search-mfe-filtersbar-price-range-button";
var roomsRangeTestId = "search-mfe-filtersbar-rooms-range-button";
var spaceRangeTestId = "search-mfe-filtersbar-space-range-button";
var FiltersBar = ({ savedModel, setSavedModel }) => {
  const { getValues, reset } = useFormContext();
  const { "breakpoint.md": breakpointMD, "breakpoint.lg": breakpointLG } = useTokens();
  const { distributionAndEstateTypeLabel, spaceLabel, otherFiltersLabel, priceLabel, roomsLabel } = useMemo(
    () => getLabels(),
    []
  );
  const { distributionAndEstateTypeTitle, spaceTitle, otherFiltersTitle, priceTitle, roomsTitle } = useMemo(
    () => getTitles(savedModel),
    [savedModel]
  );
  const submitButton = useSubmitButton("submit");
  const filterModalProps = {
    onOpen: () => setSavedModel(cloneDeep(getValues())),
    onCancel: () => reset(savedModel),
    onValidate: () => setSavedModel(getValues()),
    title: useTranslate("modals.filters.title.default"),
    footerEndButton: { ...submitButton }
  };
  const alignedOnContainerMD = css`
    display: none;
    @container (min-width: ${breakpointMD}px) {
      display: block;
    }
  `;
  const alignedOnContainerLG = css`
    display: none;
    @container (min-width: ${breakpointLG}px) {
      display: block;
    }
  `;
  return /* @__PURE__ */ jsxs(Box, { display: "flex", gap: spacing, flexDirection: "row", flexWrap: "nowrap", children: [
    /* @__PURE__ */ jsx(DefaultNoneDisplayedBox, { unsafeClassName: alignedOnContainerMD, children: /* @__PURE__ */ jsx(
      FiltersModal,
      {
        ...filterModalProps,
        trigger: (props) => /* @__PURE__ */ jsx(
          FilterButton,
          {
            label: distributionAndEstateTypeLabel,
            value: distributionAndEstateTypeTitle,
            testId: distributionTypeTestId,
            ...props
          }
        )
      }
    ) }),
    /* @__PURE__ */ jsx(DefaultNoneDisplayedBox, { unsafeClassName: alignedOnContainerLG, children: /* @__PURE__ */ jsx(
      FiltersModal,
      {
        ...filterModalProps,
        scrollInto: "priceRange",
        trigger: (props) => /* @__PURE__ */ jsx(FilterButton, { label: priceLabel, value: priceTitle, testId: priceRangeTestId, ...props })
      }
    ) }),
    /* @__PURE__ */ jsx(DefaultNoneDisplayedBox, { unsafeClassName: alignedOnContainerLG, children: /* @__PURE__ */ jsx(
      FiltersModal,
      {
        ...filterModalProps,
        scrollInto: "roomsRange",
        trigger: (props) => /* @__PURE__ */ jsx(FilterButton, { label: roomsLabel, value: roomsTitle, testId: roomsRangeTestId, ...props })
      }
    ) }),
    /* @__PURE__ */ jsx(DefaultNoneDisplayedBox, { unsafeClassName: alignedOnContainerLG, children: /* @__PURE__ */ jsx(
      FiltersModal,
      {
        ...filterModalProps,
        scrollInto: "spaceRange",
        trigger: (props) => /* @__PURE__ */ jsx(FilterButton, { label: spaceLabel, value: spaceTitle, testId: spaceRangeTestId, ...props })
      }
    ) }),
    /* @__PURE__ */ jsx(Box, { flexGrow: { "breakpoint.base": 1, "breakpoint.sm": 0 }, children: /* @__PURE__ */ jsx(
      FiltersModal,
      {
        ...filterModalProps,
        trigger: (props) => /* @__PURE__ */ jsx(
          FilterButton,
          {
            label: otherFiltersLabel,
            value: otherFiltersTitle,
            icon: SlidersIcon,
            testId: filterTestId,
            ...props
          }
        )
      }
    ) })
  ] });
};
var VariantA = () => {
  const { getValues } = useFormContext();
  const [savedModel, setSavedModel] = useState(getValues());
  return /* @__PURE__ */ jsxs(Box, { display: "flex", flexDirection: "row", columnGap: "spacing.16", children: [
    /* @__PURE__ */ jsx(MultiLocationAutocompleteModal, { savedModel, setSavedModel }),
    /* @__PURE__ */ jsx(FiltersBar, { savedModel, setSavedModel })
  ] });
};
var FusedFiltersBar = ({ savedModel, setSavedModel }) => {
  const { getValues, reset } = useFormContext();
  const [locationTitle, setLocationTitle] = useState("");
  const [distributionTypeTitle, setDistributionTypeTitle] = useState("");
  const [estateTypeTitle, setEstateTypeTitle] = useState("");
  const { otherFiltersLabel } = getLabels();
  const submitButton = useSubmitButton("submit");
  useEffect(() => {
    const locationLabel = savedModel.locations[0]?.labels[0] || "";
    setLocationTitle(locationLabel);
    setDistributionTypeTitle(
      useTranslate(`filters.distribution_type.button_group.${savedModel.distributionType.toLowerCase()}`) || ""
    );
    setEstateTypeTitle(useTranslate(`filters.estate_type.checkbox.${savedModel.estateType.toLowerCase()}`) || "");
  }, [savedModel]);
  const filterModalProps = {
    onOpen: () => setSavedModel(getValues()),
    onCancel: () => reset(savedModel),
    onValidate: () => setSavedModel(getValues()),
    footerEndButton: submitButton,
    title: useTranslate("modals.filters.title.default")
  };
  const content = /* @__PURE__ */ jsxs(Box, { display: "flex", flexDirection: "row", justifyContent: "space-between", children: [
    /* @__PURE__ */ jsxs(Box, { display: "flex", flexDirection: "row", alignItems: "center", gap: "spacing.4", children: [
      /* @__PURE__ */ jsx(Box, { typography: "typography.body.16.bold", children: locationTitle }),
      /* @__PURE__ */ jsx(Box, { display: "flex", alignItems: "end", justifyContent: "center", height: "sizing.16", children: "." }),
      /* @__PURE__ */ jsxs(Box, { typography: "typography.body.16.regular", children: [
        " ",
        distributionTypeTitle
      ] }),
      /* @__PURE__ */ jsx(Box, { display: "flex", alignItems: "end", justifyContent: "center", height: "sizing.16", children: "." }),
      /* @__PURE__ */ jsxs(Box, { typography: "typography.body.16.regular", children: [
        " ",
        estateTypeTitle
      ] })
    ] }),
    /* @__PURE__ */ jsx(SlidersIcon, {})
  ] });
  return /* @__PURE__ */ jsx(
    FiltersModal,
    {
      ...filterModalProps,
      trigger: (props) => /* @__PURE__ */ jsx(FilterButton, { icon: MagnifyingGlassIcon, label: otherFiltersLabel, ...props, testId: filterTestId, children: content })
    }
  );
};
var MobileVariantB = () => {
  const { getValues } = useFormContext();
  const [savedModel, setSavedModel] = useState(() => getValues());
  return /* @__PURE__ */ jsx(Box, { display: "flex", flexDirection: "row", columnGap: "spacing.8", children: /* @__PURE__ */ jsx(Box, { minWidth: 0, width: "100%", children: /* @__PURE__ */ jsx(FusedFiltersBar, { savedModel, setSavedModel }) }) });
};
var DesktopVariantB = () => {
  const { getValues } = useFormContext();
  const [savedModel, setSavedModel] = useState(() => getValues());
  return /* @__PURE__ */ jsxs(Box, { display: "flex", flexDirection: "row", columnGap: "spacing.8", children: [
    /* @__PURE__ */ jsx(MultiLocationAutocompleteModal, { savedModel, setSavedModel }),
    /* @__PURE__ */ jsx(FiltersBar, { savedModel, setSavedModel })
  ] });
};
var VariantB = () => {
  const isMobile = useIsMobile();
  return isMobile ? /* @__PURE__ */ jsx(MobileVariantB, {}) : /* @__PURE__ */ jsx(DesktopVariantB, {});
};
var FullSearchVariant = () => {
  const variant = useVariant();
  if (variant === "flagship") {
    return /* @__PURE__ */ jsx(VariantA, {});
  }
  return /* @__PURE__ */ jsx(VariantB, {});
};
var SearchFullSkeletontestId = "searchmfe-search-full-skeleton-testid";
var SearchFullTemplate = () => {
  useConditionalFilters();
  const isUpdated = useUpdateLocations();
  return /* @__PURE__ */ jsx(ModalProvider, { children: isUpdated ? /* @__PURE__ */ jsx(FullSearchVariant, {}) : /* @__PURE__ */ jsx(Skeleton, { width: "100%", height: 48, testId: SearchFullSkeletontestId }) });
};
var SearchFullWithMFEContext = ({ initialModel, onSubmit }) => {
  const templates = useTemplates();
  const transformers = useTransformers();
  return /* @__PURE__ */ jsx(
    WithFormProvider,
    {
      initialModel: mapSearchModelToFormModel(initialModel, templates),
      onSubmit: async (data) => {
        await onSubmit(mapFormModelToSearchModel(data, templates, transformers));
      },
      children: /* @__PURE__ */ jsx(SearchProvider, { children: /* @__PURE__ */ jsx(SearchFullTemplate, {}) })
    }
  );
};
var SearchFull = ({ stage, brand, locale, countMode, searchType, ...rest }) => /* @__PURE__ */ jsx(WithMFEContext, { brand, locale, searchType, stage, countMode, children: /* @__PURE__ */ jsx(SearchFullWithMFEContext, { ...rest }) });
var enrichLocations = async (locations, bffUrl) => {
  const enrichedLocations = await Promise.all(
    locations.map(async (location) => {
      if (location.type === LocationType.COMMUTE) {
        const places = await fetchPlaces(location.placeIds, bffUrl);
        const coords = places?.[0].coordinates;
        if (!coords?.lat || !coords?.lng) {
          return location;
        }
        const polylines = await getPolylines(
          {
            id: location.placeIds?.[0],
            commuteDuration: location.duration,
            commuteMode: location?.mode,
            coordinates: coords
          },
          bffUrl
        );
        return {
          ...location,
          polylines
        };
      }
      return location;
    })
  );
  return enrichedLocations;
};

// src/helpers/utils/enrichSearchModel/enrichSearchModel.ts
var enrichSearchModel = async (searchModel, stage, brand, isServerSide = false) => {
  const bffUrl = isServerSide ? getInternalServiceUrl(stage, brand) : getServiceUrl(stage, brand);
  try {
    const enrichedLocations = await enrichLocations(searchModel.locations, bffUrl);
    return {
      ...searchModel,
      locations: enrichedLocations
    };
  } catch {
    return searchModel;
  }
};

export { SearchFull, enrichSearchModel };
//# sourceMappingURL=out.js.map
//# sourceMappingURL=full.js.map